// src/utils/cartOptimizer.js
//
// Choosing WHICH copy a cart line buys, when the store holds several. (TKT-2026-105225)
//
// A store can hold the same card as NM Normal, LP Normal, NM Foil and so on, each
// its own copy at its own price. (Not each its own SKU, as this said until
// TKT-2026-105367: a graded slab is stored against the raw card's skuId, so the
// identity of a copy is skuId + condAbbr + printing + langAbbr, never the skuId
// on its own.) The kiosk has always picked the first of those in
// a fixed order (best condition, then Normal printing) and left the shopper to
// change it card by card in the variant dropdown. This module lets the shopper
// state a preference once instead: lowest price, foil or non-foil, nothing below
// a given condition.
//
// Three rules hold everywhere in here, and each one is load-bearing:
//
//  1. It only ever RE-PICKS inside the copies the kiosk is already offering. The
//     candidate list is exactly the in-store variants the server just served,
//     which have already been through the per-product in-store cap and the
//     channel reservation. A shopper preference can never surface a copy the
//     store excluded, which is what keeps "minimum condition" from becoming a way
//     to reach stock the owner does not sell in store.
//  2. It never silently drops a line. A card with no copy that meets the shopper's
//     floor stays in the cart on the copy it already had, and is reported back so
//     the UI can say so.
//  3. The copy it picks is the copy checkout reserves. The cart line carries
//     skuId + condAbbr + printing and the order route matches on all three, so
//     rewriting the line's variant rewrites what gets decremented. There is no
//     path here that changes what is displayed without changing what is sold.

import { variantKey, sameVariant } from './variantKey';
import { cleanUrl, variantScanImage } from './cardArt';

/** Best-first condition ranking, matching the kiosk grid and listing allocation. */
export const CONDITION_LADDER = ['NM', 'LP', 'MP', 'HP', 'DM'];

/**
 * Printings that denote a foil/premium finish. Mirrors FOIL_SUBTYPES in the
 * backend filter registry; inventory `printing` is derived from the catalog
 * price subtype, so the vocabularies are the same.
 */
export const FOIL_PRINTINGS = ['Foil', 'Reverse Holofoil', 'Cold Foil', 'Holofoil', 'Etched'];

/** The shopper preference shape. These defaults ARE the kiosk's existing behaviour. */
export const DEFAULT_PREFERENCES = Object.freeze({
  price: 'default',      // 'default' (best condition first) | 'cheapest'
  finish: 'any',         // 'any' | 'foil' | 'nonfoil'
  minCondition: 'any',   // 'any' | one of CONDITION_LADDER
});

/** True when no preference has been expressed, so there is nothing to apply. */
export function isDefaultPreferences(prefs) {
  const p = { ...DEFAULT_PREFERENCES, ...(prefs || {}) };
  return p.price === DEFAULT_PREFERENCES.price
    && p.finish === DEFAULT_PREFERENCES.finish
    && p.minCondition === DEFAULT_PREFERENCES.minCondition;
}

/**
 * Strip out any axis the store has withheld, so a preference left over from
 * before an owner turned an axis off can never keep steering the picks.
 *
 * @param {Object} prefs shopper preference
 * @param {Object} allowed { allowCost, allowFinish, allowCondition } from the store config
 */
export function clampPreferencesToAllowed(prefs, allowed) {
  const p = { ...DEFAULT_PREFERENCES, ...(prefs || {}) };
  const a = allowed || {};
  return {
    price: a.allowCost ? p.price : DEFAULT_PREFERENCES.price,
    finish: a.allowFinish ? p.finish : DEFAULT_PREFERENCES.finish,
    minCondition: a.allowCondition ? p.minCondition : DEFAULT_PREFERENCES.minCondition,
  };
}

export function isFoilPrinting(printing) {
  return FOIL_PRINTINGS.includes(String(printing || ''));
}

/** Ladder position, or -1 for a condition that is not on the ladder at all. */
export function conditionRank(condAbbr) {
  return CONDITION_LADDER.indexOf(String(condAbbr || '').toUpperCase());
}

/**
 * The price the kiosk would charge for this copy, resolved the same way the add
 * to cart path resolves it. A copy with no usable price is worth 0, and 0 is
 * treated as "not really for sale" below rather than as the best deal going.
 */
export function resolveVariantPrice(variant) {
  const raw = variant?.price ?? variant?.midPrice ?? variant?.marketPrice ?? variant?.lowPrice;
  const num = parseFloat(raw);
  return Number.isFinite(num) ? num : 0;
}

/**
 * Does this copy clear the shopper's condition floor?
 *
 * A condition that is not on the NM..DM ladder (sealed product is `U`, and stores
 * do use their own abbreviations) is never excluded by a floor. A floor is a
 * statement about card wear, and applying it to something that was never graded
 * for wear would quietly empty a sealed box out of the candidate list.
 */
export function meetsConditionFloor(variant, minCondition) {
  if (!minCondition || minCondition === 'any') return true;
  const floor = conditionRank(minCondition);
  if (floor < 0) return true;
  const rank = conditionRank(variant?.condAbbr);
  if (rank < 0) return true;
  return rank <= floor;
}

/**
 * Order two candidates for a given preference. Lower sorts first.
 *
 * The keys are applied in order and the first one that separates them wins:
 *
 *   1. priced          a copy with no price (or 0) never wins. A 0 stock row is
 *                      unsellable, and "optimise by cost" must not hand one over
 *                      for nothing.
 *   2. finish          a soft preference. If the shopper prefers foil and the
 *                      store has no foil, they still get the card.
 *   3. the shopper's primary axis, either cheapest first or the kiosk's existing
 *      best condition first.
 *   4. skuId           so the same cart, optimised twice, gives the same answer.
 */
export function compareCandidates(a, b, prefs) {
  const p = { ...DEFAULT_PREFERENCES, ...(prefs || {}) };

  const priceA = resolveVariantPrice(a);
  const priceB = resolveVariantPrice(b);
  const pricedA = priceA > 0 ? 0 : 1;
  const pricedB = priceB > 0 ? 0 : 1;
  if (pricedA !== pricedB) return pricedA - pricedB;

  if (p.finish === 'foil' || p.finish === 'nonfoil') {
    const wantFoil = p.finish === 'foil';
    const missA = isFoilPrinting(a?.printing) === wantFoil ? 0 : 1;
    const missB = isFoilPrinting(b?.printing) === wantFoil ? 0 : 1;
    if (missA !== missB) return missA - missB;
  }

  const rankA = conditionRank(a?.condAbbr);
  const rankB = conditionRank(b?.condAbbr);
  // An unrecognised condition sorts after every ladder condition, as it does on
  // the grid and in listing allocation.
  const condA = rankA < 0 ? CONDITION_LADDER.length : rankA;
  const condB = rankB < 0 ? CONDITION_LADDER.length : rankB;
  const normalA = a?.printing === 'Normal' ? 0 : 1;
  const normalB = b?.printing === 'Normal' ? 0 : 1;

  if (p.price === 'cheapest') {
    if (priceA !== priceB) return priceA - priceB;
    if (condA !== condB) return condA - condB;
    if (normalA !== normalB) return normalA - normalB;
  } else {
    if (condA !== condB) return condA - condB;
    if (normalA !== normalB) return normalA - normalB;
    if (priceA !== priceB) return priceA - priceB;
  }

  return String(a?.skuId ?? '').localeCompare(String(b?.skuId ?? ''));
}

/**
 * Pick the copy a line should buy.
 *
 * @param {Array} variants   in-store variants for the product, as served
 * @param {Object} prefs     shopper preference
 * @param {Object} [options]
 * @param {string} [options.langAbbr] the language the line currently holds
 * @returns {{variant: Object|null, reason: string|null}}
 *          reason is 'unavailable' when the store holds no buyable copy at all,
 *          'condition' when copies exist but none clears the floor.
 */
export function chooseVariant(variants, prefs, options = {}) {
  const inStock = (Array.isArray(variants) ? variants : []).filter(v => Number(v?.quantity) > 0);
  if (!inStock.length) return { variant: null, reason: 'unavailable' };

  // Language is pinned, not optimised. A cheaper Japanese copy of an English card
  // is a different physical object, and swapping one in because it costs less is
  // a surprise nobody asked for. The shopper can still change language by hand in
  // the variant dropdown. Only when the line's language has sold out entirely do
  // we widen back to everything, so the line still gets a live copy.
  const langAbbr = options.langAbbr;
  const sameLanguage = langAbbr
    ? inStock.filter(v => (v?.langAbbr || 'EN') === langAbbr)
    : [];
  const pool = sameLanguage.length ? sameLanguage : inStock;

  // The condition floor is a hard filter, unlike the finish preference. "Nothing
  // below LP" is a bar the shopper set, so we would rather leave the line alone
  // and say we could not meet it than quietly hand them an MP copy.
  const eligible = pool.filter(v => meetsConditionFloor(v, prefs?.minCondition));
  if (!eligible.length) return { variant: null, reason: 'condition' };

  const sorted = [...eligible].sort((a, b) => compareCandidates(a, b, prefs));
  return { variant: sorted[0], reason: null };
}

// Whether the re-pick actually moved the line to a different copy. Comparing
// skuId alone under-counted: switching a line from a raw NM copy to the graded
// slab of the same card keeps the skuId and is very much a switch, and it is the
// switch that changes the price most. TKT-2026-105367.
const samePick = (a, b) => sameVariant(a, b);

/**
 * Re-pick every sell line against fresh stock, then reconcile the result.
 *
 * @param {Array} items             sell-mode cart items ONLY. Buy-mode lines are a
 *        buylist offer on a printing/condition the shopper chose to sell us, not
 *        a copy we get to pick, so they never come through here.
 * @param {Object} variantsByProductId  productId (as string) -> live variants.
 *        A key present with an empty array means "the store no longer has this".
 *        A key ABSENT means "not refreshed", and the line is left untouched.
 * @param {Object} prefs            shopper preference
 * @returns {{items: Array, summary: Object}}
 */
export function optimizeCart(items, variantsByProductId, prefs) {
  const source = Array.isArray(items) ? items : [];
  const map = variantsByProductId || {};
  const summary = {
    switched: 0,
    merged: 0,
    reduced: [],
    blocked: [],
    priceDelta: 0,
  };

  const before = source.reduce((sum, i) => sum + (Number(i.price) || 0) * (Number(i.qty) || 0), 0);

  const repicked = source.map((item) => {
    const key = String(item?.productId ?? '');
    if (!Object.prototype.hasOwnProperty.call(map, key)) return item;

    const { variant, reason } = chooseVariant(map[key], prefs, {
      langAbbr: item?.variant?.langAbbr || 'EN',
    });

    if (!variant) {
      summary.blocked.push({ cleanName: item.cleanName, reason });
      return item;
    }

    // Even when the pick is the copy the line already had, take the refreshed
    // row: its quantity is the live in-store availability, and the clamp below
    // is the only thing standing between a stale cart and an oversell.
    const price = resolveVariantPrice(variant);
    if (!samePick(variant, item?.variant)) summary.switched += 1;

    return {
      ...item,
      variant,
      price,
      // Prefer the chosen copy's own scan, then the art for ITS printing, which
      // the backend sends resolved on every variant (TKT-2026-107421): the copy
      // picked can be another printing than the one the line was added as.
      // `catalogImage` is the card's stock image, captured when the line was
      // added; lines added before this feature shipped do not carry one, so they
      // keep whatever image they had rather than losing the thumbnail entirely.
      cdnImage: variantScanImage(variant) || cleanUrl(variant.catalogImage) || item.catalogImage || item.cdnImage || '',
    };
  });

  // Two lines of the same card (say an NM and an LP copy) can be re-picked onto
  // the same copy, which would otherwise leave the cart holding it twice. They
  // have to become one line, and the merged quantity is what has to be checked
  // against stock.
  //
  // The key is the full variant identity, not the SKU. A graded slab shares the
  // raw card's skuId, so merging on skuId folded a $102 slab line into a $5 raw
  // line and billed both at one price. TKT-2026-105367.
  const byKey = new Map();
  for (const item of repicked) {
    const key = variantKey(item?.variant);
    const existing = byKey.get(key);
    if (existing) {
      existing.qty = (Number(existing.qty) || 0) + (Number(item.qty) || 0);
      summary.merged += 1;
    } else {
      byKey.set(key, { ...item, qty: Number(item.qty) || 0 });
    }
  }

  const finalItems = [];
  for (const item of byKey.values()) {
    const available = Number(item?.variant?.quantity);
    const wanted = Math.max(1, Number(item.qty) || 1);
    const qty = Number.isFinite(available) && available > 0 ? Math.min(wanted, available) : wanted;
    if (qty !== wanted) summary.reduced.push({ cleanName: item.cleanName, from: wanted, to: qty });
    finalItems.push({ ...item, qty });
  }

  const after = finalItems.reduce((sum, i) => sum + (Number(i.price) || 0) * (Number(i.qty) || 0), 0);
  summary.priceDelta = Math.round((after - before) * 100) / 100;

  return { items: finalItems, summary };
}

/**
 * The copy a freshly added card should default to, so a preference set in the
 * cart also governs what the next tap of Add adds. Falls back to the kiosk's
 * existing first-in-sorted-order pick when nothing is preferred or nothing fits.
 */
export function preferredVariantForAdd(variants, prefs, fallback) {
  if (!Array.isArray(variants) || !variants.length) return fallback ?? null;
  if (isDefaultPreferences(prefs)) return fallback ?? variants[0];
  const { variant } = chooseVariant(variants, prefs, {});
  return variant || fallback || variants[0];
}
